import { extendTheme, ThemeConfig } from '@chakra-ui/react'

import type { ComponentStyleConfig } from '@chakra-ui/theme'
import { StepsStyleConfig } from 'chakra-ui-steps'

const config: ThemeConfig = {
    initialColorMode: 'light',
    useSystemColorMode: false
}

const breakpoints = {
    xs: '20em',
    sm: '36em'
}

const colors = {
    primary: {
        50: 'transparent',
        100: 'transparent',
        300: '#21437a',
        400: '#1c3a6a',
        500: '#18315A',
        600: '#14284a',
        700: '#0f1f3a'
    },
    lime: {
        50: '#F7F7F5',
        300: '#DFFFB2',
        500: '#87FA38'
    },
    'off-white': {
        50: '#FFFFFF',
        100: '#FFFFFF',
        200: '#FFFFFF',
        300: '#FFFFFF',
        400: '#FFFFFF',
        500: '#FFFFFF',
        600: '#FFFFFF',
        700: '#FFFFFF',
        800: '#FFFFFF',
        900: '#FFFFFF'
    },
    backdrop: '#F4F5FB',
    grayscale: {
        50: '#F5F5F5',
        100: '#ECECEC',
        500: '#ECECEC',
        800: '#888891',
        950: '#0C0B0E'
    },
    info: {
        500: '#18315A'
    },
    error: {
        500: '#CB1F1F'
    }
}

const shadows = {
    md: `4px 4px 8px rgba(0, 0, 0, 0.08)`
}

const Button: ComponentStyleConfig = {
    variants: {
        solid: props => ({
            ...(props.colorScheme === 'primary'
                ? {
                      bg: 'primary.500',
                      color: 'white',
                      _disabled: {
                          color: 'white!important'
                      },
                      _hover: {
                          color: 'primary.500',
                          bg: 'lime.500'
                      },
                      _active: {
                          color: 'primary.500',
                          bg: 'lime.300'
                      }
                  }
                : {})
        }),
        outline: props => ({
            ...(props.colorScheme === 'primary'
                ? {
                      color: 'primary.500',
                      borderColor: 'primary.500',
                      _hover: {
                          borderColor: 'lime.500'
                      },
                      _active: {
                          borderColor: 'lime.300'
                      }
                  }
                : {})
        })
    },
    defaultProps: {
        size: 'sm'
    }
}

const Checkbox: ComponentStyleConfig = {
    baseStyle: {
        control: {
            _disabled: {
                bg: 'var(--chakra-colors-grayscale-50)',
                borderColor: 'var(--chakra-colors-grayscale-800)'
            },
            _hover: {
                borderColor: 'var(--chakra-colors-lime-500)!important',
                _checked: {
                    bg: 'var(--chakra-colors-lime-500)!important'
                }
            }
        },
        label: {
            color: 'var(--chakra-colors-grayscale-950)',
            _disabled: {
                opacity: 1
            }
        }
    },
    defaultProps: {
        colorScheme: 'primary'
    }
}

const Badge: ComponentStyleConfig = {
    sizes: {
        xl: {
            padding: '0.25rem .5rem',
            borderRadius: '0.5rem'
        }
    },
    defaultProps: {
        variant: 'solid'
    }
}

const Spinner: ComponentStyleConfig = {
    sizes: {
        xl: {
            width: '64px',
            height: '64px'
        },
        sm: {
            width: '32px',
            height: '32px'
        }
    },
    defaultProps: {
        variant: 'solid'
    }
}

const Modal: ComponentStyleConfig = {}

const Table: ComponentStyleConfig = {
    variants: {
        striped: {
            table: {
                'tbody tr:nth-of-type(odd) td': {
                    background: 'var(--chakra-colors-grayscale-50) !important',
                    borderColor: 'var(--chakra-colors-grayscale-50) !important'
                }
            },
            thead: {
                height: '40px'
            },
            th: {
                color: '',
                fontFamily: '',
                fontSize: 'inherit',
                textTransform: '',
                letterSpacing: '',
                lineHeight: ''
            },
            td: {
                borderColor: 'transparent'
            }
        }
    },
    sizes: {
        sm: {
            tr: {
                height: '49px'
            }
        }
    },
    defaultProps: {
        size: 'sm'
    }
}

const FieldBase = {
    baseStyle: {
        field: {
            color: '#0C0B0E',
            _disabled: {
                background: '#ECECEC',
                borderColor: '#888891!important',
                opacity: '1!important',
                _hover: {
                    borderColor: '#888891'
                }
            },
            _readOnly: {
                background: '#ECECEC',
                borderColor: '#888891!important',
                _hover: {
                    borderColor: '#888891'
                }
            },
            _placeholder: {
                color: '#888891'
            }
        }
    },
    variants: {
        flushed: {
            field: {
                borderColor: '#888891',
                _focusVisible: {
                    borderColor: 'var(--chakra-colors-primary-500)',
                    boxShadow:
                        '0px 1px 0px 0px var(--chakra-colors-primary-500)'
                },
                _hover: {
                    borderColor: 'var(--chakra-colors-primary-300)'
                }
            }
        },
        outline: {
            field: {
                borderColor: '#888891',
                _hover: {
                    borderColor: 'var(--chakra-colors-primary-300)'
                },
                _focusVisible: {
                    borderColor: 'var(--chakra-colors-primary-500)',
                    boxShadow:
                        '0px 0px 0px 1px var(--chakra-colors-primary-500)'
                }
            }
        }
    }
}

const Input: ComponentStyleConfig = {
    ...FieldBase
}

const Select: ComponentStyleConfig = {
    ...FieldBase
}

const PinInput: ComponentStyleConfig = {
    baseStyle: {
        ...FieldBase.baseStyle.field
    },
    variants: {
        flushed: {
            ...FieldBase.variants.flushed.field
        },
        outline: {
            ...FieldBase.variants.outline.field
        }
    }
}

const Steps: ComponentStyleConfig = {
    ...StepsStyleConfig,
    baseStyle: props => {
        return {
            ...StepsStyleConfig.baseStyle(props),
            steps: {
                ...StepsStyleConfig.baseStyle(props).steps,
                minHeight: '64px',
                paddingBlock: '24px'
            },
            step: {
                ...StepsStyleConfig.baseStyle(props).step
            },
            stepIconContainer: {
                ...StepsStyleConfig.baseStyle(props).stepIconContainer,
                display: 'none'
            },
            labelContainer: {
                ...StepsStyleConfig.baseStyle(props).labelContainer,
                span: {
                    fontSize: 'var(--chakra-fontSizes-sm)',
                    color: 'inherit'
                }
            },
            connector: {
                ...StepsStyleConfig.baseStyle(props).connector
            }
        }
    },
    variants: {
        short: {
            steps: {
                justifyContent: 'flex-start'
            },
            step: {
                flex: 'none'
            },
            connector: {
                minWidth: '30px'
            }
        }
    }
}

const Tooltip = {
    baseStyle: {
        bg: 'var(--chakra-colors-black) !important',
        color: 'var(--chakra-colors-white)',
        padding: '.5rem',
        borderRadius: '.25rem',
        placement: {
            background: 'red'
        }
    }
}

const Switch: ComponentStyleConfig = {
    baseStyle: {
        track: {
            background: '#888891',
            _checked: {
                bg: 'primary.500'
            }
        }
    }
}

const Alert: ComponentStyleConfig = {
    variants: {
        'solid-error': {
            container: {
                bg: 'error.500',
                color: 'white'
            }
        },
        'solid-info': {
            container: {
                bg: 'info.500',
                color: 'white'
            }
        }
    },
    defaultProps: {
        variant: 'solid'
    }
}
const isViewContractQRCode = window.location.href.includes(
    'visualizar-contrato-qr-code'
)

export const theme = extendTheme({
    colors,
    shadows,
    config,
    breakpoints,
    styles: {
        global: () => ({
            html: {
                height: '100vh',
                overflow: !isViewContractQRCode && 'hidden'
            },
            body: {
                fontFamily: 'Inter'
            },

            '&::-webkit-scrollbar': {
                width: '5px',
                height: '5px'
            },
            '&::-webkit-scrollbar-thumb': {
                background: '#0C0B0E',
                borderRadius: '1rem'
            },
            '&::-webkit-scrollbar-track-piece': {
                background: '#ECECEC',
                borderRadius: '1rem'
            }
        })
    },
    components: {
        Alert,
        Badge,
        Button,
        Input,
        Checkbox,
        PinInput,
        Select,
        Steps,
        Switch,
        Modal,
        Table,
        Tooltip,
        Spinner
    }
})
